import cx from "classnames";
import React, { useMemo } from "react";
import { generatePath } from "react-router";
import { Beacon } from "../../../../../components/beacon/Beacon";
import { Visibility } from "../../../../../components/icons/Visibility";
import { Link } from "../../../../../components/links/Link";
import {
  Associate,
  SigningMethod,
} from "../../../../../data/models/AssociateTypes";
import { ContractId } from "../../../../../data/models/CommonTypes";
import {
  BankAccount,
  AssociateTracking,
  CommunicationEvent,
  Validity,
  Contract,
  ContractStatus,
  BankAccountSource,
} from "../../../../../data/models/ContractTypes";
import { ASSOCIATE_REVIEW_PAGE } from "../../AssociateReview/AssociateReviewPage";
import { TIMELINE_ASSOCIATE } from "../Timeline";
import { T } from "../../../../../components/translation/T";
import { getIntlDate } from "../../../../../components/utils";
import "./EventHAssociate.scss";
import {
  isAccountHolder,
  isOwner,
  isSignee,
} from "../../../../../components/associateUtils";

interface Props {
  contract: Contract;
  contractId: ContractId;
  associate: Associate;
  bankAccount?: BankAccount;
  associateTracking?: AssociateTracking;
}

function getSigningMethod(method: SigningMethod) {
  switch (method) {
    case SigningMethod.BANKID:
      return "BankId";
    case SigningMethod.MITID:
      return "MitID";
    case SigningMethod.FTN:
      return "Finnish Trust Network";
    case SigningMethod.REMOTE_ID:
      return "Remote ID";

    default:
      return "-";
  }
}

function buildHeader(associate: Associate) {
  const header = [];

  if (isSignee(associate)) {
    header.push("Legal representative");
  }
  if (isOwner(associate)) {
    header.push("Beneficial owner");
  }
  if (isAccountHolder(associate)) {
    header.push("Added bank account");
  }
  return header.join(", ");
}

function getSource(bankAccount?: BankAccount) {
  switch (bankAccount?.source) {
    case BankAccountSource.PSD_LOOKUP:
      return "Added account by external bank login";
    case BankAccountSource.OFFICIAL_REGISTRY:
      return "Added account by official registry";
    case BankAccountSource.BANK_STATEMENT:
      return "Added account by bank statement";
    case BankAccountSource.BANKGIRO:
      return "Added by merchant/sales through Bankgiro";
    default:
      return "Added bank account";
  }
}

function getAddBankAccountStatusForSingleAccountHolder(
  bankAccount?: BankAccount
): {
  validity: Validity;
  message: string;
}[] {
  if (!bankAccount || !bankAccount.source) {
    return [
      {
        validity: Validity.MISSING,
        message: getSource(bankAccount),
      },
    ];
  }

  if (bankAccount.signedOff) {
    return [
      {
        validity: Validity.VALID,
        message: getSource(bankAccount),
      },
    ];
  }

  if (bankAccount.source === BankAccountSource.BANK_STATEMENT) {
    if (bankAccount.statementUploaded) {
      return [
        {
          validity: Validity.VALID,
          message: getSource(bankAccount),
        },
        {
          validity: Validity.PARTIAL,
          message: "Bank account awaiting sales confirmation",
        },
      ];
    } else {
      return [
        {
          validity: Validity.VALID,
          message: getSource(bankAccount),
        },
        {
          validity: Validity.MISSING,
          message: "Missing mandatory bank statement upload",
        },
      ];
    }
  }

  if (bankAccount.source === BankAccountSource.BANKGIRO) {
    if (!bankAccount.iban) {
      return [
        {
          validity: Validity.MISSING,
          message: getSource(bankAccount),
        },
      ];
    }

    // if resolved, Bankgiro validation must have failed
    // otherwise it would have been signed off.
    if (bankAccount.bgcStatus?.resolved) {
      return [
        {
          validity: Validity.VALID,
          message: getSource(bankAccount),
        },
        {
          validity: Validity.MISSING,
          message: "Account rejected by Bankgiro",
        },
      ];
    } else {
      return [
        {
          validity: Validity.VALID,
          message: getSource(bankAccount),
        },
        {
          validity: Validity.PARTIAL,
          message: "Awaiting validation by Bankgiro",
        },
      ];
    }
  }

  return [
    {
      validity: Validity.MISSING,
      message: getSource(bankAccount),
    },
  ];
}

function getAddBankAccountStatusForPersonWithMultipleRoles(
  bankAccount: BankAccount
): {
  validity: Validity;
  message: string;
} {
  // BankAccountSource.PSD_LOOKUP || BankAccountSource.OFFICIAL_REGISTRY
  if (bankAccount.signedOff) {
    return {
      validity: Validity.VALID,
      message: "Added bank account",
    };
  }

  if (
    bankAccount?.source === BankAccountSource.BANK_STATEMENT &&
    bankAccount.statementUploaded
  ) {
    return {
      validity: Validity.PARTIAL,
      message: "Added bank account (awaiting sales confirmation)",
    };
  }

  if (bankAccount.source === BankAccountSource.BANKGIRO) {
    if (!bankAccount.iban) {
      return {
        validity: Validity.MISSING,
        message: "Added bank account",
      };
    }

    // if resolved, Bankgiro validation must have failed
    return bankAccount.bgcStatus?.resolved
      ? {
          validity: Validity.MISSING,
          message: "Added bank account (Bankgiro verification failed)",
        }
      : {
          validity: Validity.PARTIAL,
          message: "Added bank account (awaiting Bankgiro response)",
        };
  }

  return {
    validity: Validity.MISSING,
    message: "Added bank account",
  };
}

export const EventHAssociate: React.FunctionComponent<Props> = ({
  contractId,
  contract,
  associate,
  bankAccount,
  associateTracking,
}) => {
  const accountHolder = isAccountHolder(associate);
  const signee = isSignee(associate);
  const isSingleAccountHolder = accountHolder && !signee;

  const logCopy = structuredClone(associateTracking?.trackingLog) || [];
  const hasViewedContract = logCopy
    .filter((item) => item.event === CommunicationEvent.CONTRACT_VIEWED)
    .sort(
      (a, b) => new Date(a.created).getTime() - new Date(b.created).getTime()
    )[0];

  const contractStatusAllowsConfirmation =
    contract.status !== ContractStatus.SALES_INPUT &&
    contract.status !== ContractStatus.MERCHANT_INPUT;

  const button = useMemo(() => {
    let enableConfirmDetails = true;

    if (contract.readOnly) {
      enableConfirmDetails = false;
    }

    if (!contractStatusAllowsConfirmation) {
      enableConfirmDetails = false;
    }

    if (associate.signedOff) {
      return null;
    }

    return (
      <Link
        className={cx("event-button", "small", "as-button", {
          "is-disabled": !enableConfirmDetails,
        })}
        link={generatePath(ASSOCIATE_REVIEW_PAGE, {
          contractId: contractId,
          associateId: associate.associateId,
        })}
      >
        <Visibility />{" "}
        {!!associate.signatory?.signed ? <T>View</T> : <T>Remind</T>}
      </Link>
    );
  }, [associate, contractId, contract, contractStatusAllowsConfirmation]);

  const accountValidity = useMemo(
    () => getAddBankAccountStatusForSingleAccountHolder(bankAccount),
    [bankAccount]
  );

  if (isSingleAccountHolder) {
    return (
      <li
        className={cx("associate-timeline-item", "text-small", {
          active: contractStatusAllowsConfirmation,
        })}
        style={{
          height: TIMELINE_ASSOCIATE,
        }}
      >
        {!contract.readOnly &&
        contract.status === ContractStatus.MERCHANT_SIGNING &&
        !bankAccount?.signedOff &&
        !bankAccount?.iban ? (
          <>{button}</>
        ) : null}
        <div className="relative">
          <div className="associate-item-name">
            {`${associate.contact.firstName} ${associate.contact.lastName}`},{" "}
            <span className="timeline-item-type">Account verifier</span>
          </div>

          {accountValidity.map((item, index) => {
            return (
              <div
                key={`${item.message}-${index}`}
                className="associate-item-signature"
              >
                <Beacon className="aligned" mini validity={item.validity} />{" "}
                <span>
                  <T>{item.message}</T>
                </span>
              </div>
            );
          })}

          {!!bankAccount?.signedOff ? (
            <div className="associate-item-signature">
              <Beacon className="aligned" mini validity={Validity.VALID} />{" "}
              <span>IBAN: {bankAccount.iban || "-"}</span>
            </div>
          ) : null}
        </div>
      </li>
    );
  }

  return (
    <li
      className={cx("associate-timeline-item", "text-small", {
        active: contractStatusAllowsConfirmation && !contract.readOnly,
      })}
      style={{
        height: TIMELINE_ASSOCIATE,
      }}
    >
      {button}
      <div className="relative">
        <div className="associate-item-name">
          {`${associate.contact.firstName} ${associate.contact.lastName}`},{" "}
          <span className="timeline-item-type">{buildHeader(associate)}</span>
        </div>

        <div className="associate-item-signature">
          <Beacon
            className="aligned"
            mini
            validity={
              associateTracking === undefined
                ? Validity.MISSING
                : hasViewedContract
                ? Validity.VALID
                : //Message sent, not viewed
                  Validity.DEFAULT
            }
          />{" "}
          <span>
            <T>Viewed contract</T> -{" "}
            {hasViewedContract ? getIntlDate(hasViewedContract.created) : null}
          </span>
        </div>

        {accountHolder && (
          <div className="associate-item-signature">
            <Beacon
              className="aligned"
              mini
              validity={
                getAddBankAccountStatusForPersonWithMultipleRoles(
                  bankAccount || {}
                ).validity
              }
            />{" "}
            <span>
              <T>
                {
                  getAddBankAccountStatusForPersonWithMultipleRoles(
                    bankAccount || {}
                  ).message
                }
              </T>
            </span>
          </div>
        )}

        <div className="associate-item-signature">
          <Beacon
            className="aligned"
            mini
            validity={
              associate.signatory?.signed ? Validity.VALID : Validity.MISSING
            }
          />{" "}
          <span>
            {associate.signatory?.signed ? (
              <>
                <T>Signed with</T>{" "}
                {getSigningMethod(
                  associate.signatory.signingMethod || SigningMethod.BANKID
                )}
              </>
            ) : (
              <T>Added signature</T>
            )}
          </span>
        </div>
      </div>
    </li>
  );
};
